import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardingNav from '../components/onboarding/OnboardingNav';
import PrimaryButton from '../components/onboarding/PrimaryButton';
import StepProgress from '../components/onboarding/StepProgress';
import { useGlobalState } from '../components/AppContext';
import { useSignupKeepPageOpen } from '../hooks/useSignupKeepPageOpen';
import { normalizeEmail } from '../utils/onboardingValidation';
import { getCallableFriendlyErrorMessage } from '../utils/callableErrors';
import { getPinnedOrDirectCallable } from '../utils/pinnedCallables';
import {
  SITE_TYPES,
  WORKSPACE_PREFIXES,
  canSubmitWorkspaceSetup,
  getWorkspaceSetupDisabledReason,
  isMobileWorkspace,
  workspaceSetupLabels,
  workspaceSetupPayload,
} from '../utils/workspaceSetup';
import { persistTrialStartedState } from '../utils/trialStartedSession';
import chevronIcon from '../assets/images/onboarding-chevron.svg';
import '../assets/css/Onboarding.css';

function SelectChevron() {
  return (
    <span className="onboarding-select-chevron" aria-hidden>
      <img src={chevronIcon} alt="" width={8} height={4} />
    </span>
  );
}

function WorkspacePrefixSelect({
  value,
  open,
  labelledBy,
  onToggle,
  onSelect,
}) {
  return (
    <div className="onboarding-slug-prefix">
      <button
        type="button"
        className="onboarding-slug-prefix-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        onClick={onToggle}
      >
        <span>{value}</span>
        <SelectChevron />
      </button>
      {open ? (
        <ul
          className="onboarding-select-menu onboarding-select-menu--prefix"
          role="listbox"
          aria-labelledby={labelledBy}
        >
          {WORKSPACE_PREFIXES.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={value === option}
                className={value === option ? 'is-selected' : undefined}
                onClick={() => onSelect(option)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * BWC-4794 — Workspace Setup (step 3 of 4).
 */
function OnboardingWorkspaceSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalEmail] = useGlobalState('email');
  const email = normalizeEmail(location.state?.email || globalEmail || '');

  const [workspacePrefix, setWorkspacePrefix] = useState('Website');
  const [workspaceName, setWorkspaceName] = useState('');
  const [siteType, setSiteType] = useState('');
  const [workspaceUrl, setWorkspaceUrl] = useState('');
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);

  const createOnboardingWorkspace = getPinnedOrDirectCallable(
    'createOnboardingWorkspace',
    { timeout: 320000 }
  );

  useSignupKeepPageOpen(loading);

  const isMobileApp = isMobileWorkspace(workspacePrefix);
  const labels = workspaceSetupLabels(workspacePrefix);
  const canSubmit = canSubmitWorkspaceSetup({
    workspaceName,
    workspacePrefix,
    siteType,
    workspaceUrl,
    loading,
  });
  const submitDisabledReason = getWorkspaceSetupDisabledReason({
    workspaceName,
    workspacePrefix,
    siteType,
    workspaceUrl,
    loading,
  });

  useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    const previous = document.body.style.cssText;
    document.body.style.cssText =
      'margin:0;padding:0;min-height:100vh;display:block;overflow-x:hidden;background:#f5f7fa;background-image:none;';
    return () => {
      document.body.style.cssText = previous;
    };
  }, []);

  useEffect(() => {
    if (!prefixOpen && !typeOpen) {
      return undefined;
    }
    const closeIfOutside = (event) => {
      if (
        event.target.closest(
          '.onboarding-select-wrap, .onboarding-slug-prefix'
        )
      ) {
        return;
      }
      setPrefixOpen(false);
      setTypeOpen(false);
    };
    document.addEventListener('pointerdown', closeIfOutside);
    return () => document.removeEventListener('pointerdown', closeIfOutside);
  }, [prefixOpen, typeOpen]);

  const handlePrefixSelect = (option) => {
    setWorkspacePrefix(option);
    // Don't carry a Website URL into the Application name field (or vice versa).
    setWorkspaceUrl('');
    setPrefixOpen(false);
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setLoading(true);
    setFormError('');
    try {
      const result = await createOnboardingWorkspace(
        workspaceSetupPayload({
          email,
          workspaceName,
          workspacePrefix,
          siteType,
          workspaceUrl,
        })
      );
      if (result?.data?.code !== 200) {
        throw new Error(result?.data?.message || 'Could not create workspace');
      }
      const trialState = {
        email,
        planName: result.data.planName,
        trialStartedAt: result.data.trialStartedAt,
        trialEndsAt: result.data.trialEndsAt,
        publishingAllowance: result.data.publishingAllowance,
        siteName: result.data.siteName,
        orgId: result.data.orgId,
        handoffUrl: result.data.handoffUrl,
      };
      persistTrialStartedState(email, trialState);
      navigate('/trial-started', {
        state: trialState,
      });
    } catch (err) {
      console.error('Create workspace error:', err);
      setFormError(
        getCallableFriendlyErrorMessage(
          err,
          'We could not create your workspace right now. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="onboarding-verify-page">
      <OnboardingNav />
      <main className="onboarding-verify-stage">
        <form
          className="onboarding-workspace-card"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="onboarding-workspace-tracker">
            <StepProgress activeStep={3} />
            <p className="onboarding-step-label">Step 3 of 4</p>
          </div>
          <div className="onboarding-workspace-copy">
            <h1 className="onboarding-workspace-title">
              Set up your workspace
            </h1>
            <p className="onboarding-workspace-subtext">
              Your workspace is where all your content lives. You can invite
              your team later.
            </p>
          </div>

          <div className="onboarding-workspace-fields">
            <div className="onboarding-field">
              <label className="onboarding-label" htmlFor="ob-workspace-name">
                {labels.name}
              </label>
              <input
                id="ob-workspace-name"
                className="onboarding-input"
                value={workspaceName}
                placeholder="Acme Commerce International"
                onChange={(e) => {
                  setWorkspaceName(e.target.value);
                  setFormError('');
                }}
                required
              />
            </div>

            <div className="onboarding-field">
              <span className="onboarding-label" id="ob-site-type-label">
                {labels.type}
              </span>
              <div className="onboarding-select-wrap">
                <button
                  type="button"
                  className={`onboarding-select-trigger ${
                    typeOpen ? 'is-open' : ''
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={typeOpen}
                  aria-labelledby="ob-site-type-label"
                  onClick={() => {
                    setTypeOpen((open) => !open);
                    setPrefixOpen(false);
                  }}
                >
                  <span>
                    {siteType || `Select ${labels.type.toLowerCase()}`}
                  </span>
                  <SelectChevron />
                </button>
                {typeOpen ? (
                  <ul
                    className="onboarding-select-menu onboarding-select-menu--full"
                    role="listbox"
                    aria-labelledby="ob-site-type-label"
                  >
                    {SITE_TYPES.map((option) => (
                      <li key={option}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={siteType === option}
                          className={
                            siteType === option ? 'is-selected' : undefined
                          }
                          onClick={() => {
                            setSiteType(option);
                            setTypeOpen(false);
                            setFormError('');
                          }}
                        >
                          {option}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            <div className="onboarding-field">
              <span className="onboarding-label" id="ob-workspace-url-label">
                {labels.slugLabel}
              </span>
              <div
                className={`onboarding-slug-field ${
                  prefixOpen ? 'is-open' : ''
                }`}
              >
                <WorkspacePrefixSelect
                  value={workspacePrefix}
                  open={prefixOpen}
                  labelledBy="ob-workspace-url-label"
                  onToggle={() => {
                    setPrefixOpen((open) => !open);
                    setTypeOpen(false);
                  }}
                  onSelect={handlePrefixSelect}
                />
                <span className="onboarding-slug-hint">{labels.slugHint}</span>
                <input
                  id="ob-workspace-url"
                  className="onboarding-slug-input"
                  value={workspaceUrl}
                  placeholder={labels.slugPlaceholder}
                  aria-labelledby="ob-workspace-url-label"
                  inputMode={isMobileApp ? 'text' : 'url'}
                  autoComplete={isMobileApp ? 'off' : 'url'}
                  onChange={(e) => {
                    setWorkspaceUrl(e.target.value);
                    setFormError('');
                  }}
                  required
                />
              </div>
            </div>
          </div>

          {formError ? (
            <p className="onboarding-error" role="alert">
              {formError}
            </p>
          ) : null}

          {loading ? (
            <p className="onboarding-helper" role="status">
              Creating your workspace — keep this tab open. This can take a
              minute.
            </p>
          ) : null}

          <div className="onboarding-workspace-actions">
            <PrimaryButton
              type="submit"
              disabled={!canSubmit}
              disabledTooltip={submitDisabledReason}
              showDisabledTooltip={showDisabledTooltip && !canSubmit}
              onShowDisabledTooltip={() => setShowDisabledTooltip(true)}
              loading={loading}
              showArrow={false}
            >
              {loading ? 'Creating Workspace…' : 'Create Workspace'}
            </PrimaryButton>
            <p className="onboarding-workspace-helper">
              You can change your workspace name later in settings.
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}

export default OnboardingWorkspaceSetup;
